import { useMemo } from "react";
import { ScanLine, Camera, Tag, Rocket, Check, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Feb-2026 — Create-Listing-Wizard (Stepper).
 *
 * Drei Schritte führen neue Inserenten durch das Formular:
 *  1. Fotos & ZB1-Scan    — KI füllt 80 % der Stammdaten automatisch
 *  2. Preise & Strategie  — Festpreis / Auktion / Sharing
 *  3. Veröffentlichen      — Live-Vorschau & Qualität prüfen
 *
 * Feb-2026 (User-Wunsch 27.05.2026) — Wizard hat KEINE eigenen Dismiss-Buttons
 * mehr. Sichtbarkeit + Dismiss-Optionen werden in der `CreateListingToolbar`
 * unter EINEM konsolidierten Dropdown-Button gesteuert (gemeinsam mit
 * Live-Vorschau + Fortschritts-Anzeige).
 */
const STEPS = [
    {
        id: 1, label: "Fotos & ZB1-Scan", short: "Fotos",
        icon: Camera, scrollTarget: "image-upload-section",
        hint: "Lade Fotos hoch und scanne deine ZB1 — die KI füllt HSN, TSN, Marke, Modell, Datum & mehr automatisch.",
        cta: "Jetzt ZB1 scannen", ctaTarget: "zb1-scan-card",
        ctaIcon: ScanLine,
        isDone: (f) => Array.isArray(f.images) && f.images.length > 0 && f.title,
    },
    {
        id: 2, label: "Preise & Strategie", short: "Preise",
        icon: Tag, scrollTarget: "basics-section",
        hint: "Festpreis, Auktion oder Sharing? Lege deine Verkaufsstrategie fest.",
        cta: "Zu Preis & Verkaufstyp", ctaTarget: "basics-section",
        ctaIcon: Tag,
        isDone: (f) => f.price && Number(f.price) > 0,
    },
    {
        id: 3, label: "Veröffentlichen", short: "Live",
        icon: Rocket, scrollTarget: "publish-section",
        hint: "Letzter Check: Live-Vorschau ansehen, Qualitäts-Score prüfen, dann veröffentlichen.",
        cta: "Jetzt veröffentlichen", ctaTarget: "submit-listing-btn",
        ctaIcon: Rocket,
        isDone: () => false,
    },
];

export default function CreateListingWizard({ form, isEdit }) {
    const stepStatus = useMemo(
        () => STEPS.map((s) => ({ ...s, done: s.isDone(form) })),
        [form]
    );
    const currentStep = useMemo(() => {
        const idx = stepStatus.findIndex((s) => !s.done);
        return idx === -1 ? STEPS.length : idx + 1;
    }, [stepStatus]);

    const pct = Math.round((stepStatus.filter((s) => s.done).length / STEPS.length) * 100);

    const jumpTo = (testid) => {
        const el = document.querySelector(`[data-testid="${testid}"]`);
        if (!el) return;
        const collapsedBtn = el.querySelector?.('[aria-expanded="false"]') || el.closest?.('[data-testid$="-toggle"]')?.querySelector('[aria-expanded="false"]');
        if (collapsedBtn) collapsedBtn.click();
        setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-[#FFCC00]", "ring-offset-2", "transition");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#FFCC00]", "ring-offset-2"), 2200);
        }, 200);
    };

    if (isEdit) return null;

    const current = stepStatus[currentStep - 1] || stepStatus[STEPS.length - 1];

    return (
        <div className="mb-8 bg-gradient-to-br from-[#FFCC00]/30 via-amber-50 to-white border-2 border-[#FFCC00] rounded-sm overflow-hidden" data-testid="create-listing-wizard">
            {/* Header / Step-Indicator. Feb-2026 (User-Wunsch 27.05.2026):
                KEINE Dismiss-Buttons mehr — Ausblenden via Toolbar-Dropdown. */}
            <div className="flex items-center justify-between px-5 pt-4">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#111827]" />
                    <h3 className="font-display font-black text-sm tracking-tight uppercase">Geführter Modus</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm font-mono-num">
                        Schritt {currentStep} / {STEPS.length}
                    </span>
                </div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">
                    Fortschritt: {pct}%
                </div>
            </div>

            {/* Stepper-Bar */}
            <div className="px-5 mt-3">
                <div className="flex items-center gap-1">
                    {stepStatus.map((s, idx) => {
                        const isCurrent = currentStep === s.id;
                        const Icon = s.icon;
                        return (
                            <div key={s.id} className="flex items-center flex-1 last:flex-initial">
                                <button
                                    type="button"
                                    onClick={() => jumpTo(s.scrollTarget)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-sm transition-colors ${isCurrent ? "bg-[#111827] text-[#FFCC00]" : s.done ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                                    data-testid={`wizard-step-${s.id}`}
                                    title={s.label}
                                >
                                    <div className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${isCurrent ? "bg-[#FFCC00] text-[#111827]" : s.done ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-500"}`}>
                                        {s.done ? <Check className="w-3 h-3" /> : <span className="text-[10px] font-black font-mono-num">{s.id}</span>}
                                    </div>
                                    <Icon className="w-3.5 h-3.5 hidden sm:inline" />
                                    <span className="text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                                        <span className="hidden md:inline">{s.label}</span>
                                        <span className="md:hidden">{s.short}</span>
                                    </span>
                                </button>
                                {idx < STEPS.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-1 transition-colors ${s.done ? "bg-emerald-400" : "bg-gray-200"}`} />
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="h-1 bg-gray-100 rounded-sm overflow-hidden mt-2">
                    <div className="h-full bg-[#FFCC00] transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
            </div>

            {/* Current-Step-Hint + CTA */}
            <div className="px-5 py-4 mt-2 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-gray-600 mb-0.5">Jetzt dran</div>
                    <p className="text-sm font-medium text-[#111827]">{current.hint}</p>
                </div>
                <Button
                    type="button"
                    onClick={() => jumpTo(current.ctaTarget)}
                    className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider whitespace-nowrap"
                    data-testid="wizard-cta"
                >
                    {current.ctaIcon ? <current.ctaIcon className="w-4 h-4 mr-1.5" /> : null}
                    {current.cta}
                    <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
            </div>
        </div>
    );
}
